import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Icon } from "../Icon";
import { useAuth } from "../auth";
import { PageHero, SectionHead, useSeo } from "../ui";

function Profile() {
  const { member, updateProfile } = useAuth();
  const [form, setForm] = useState({
    name: member.name || "", phone: member.phone || "", company: member.company || "",
    marketing_agree: !!member.marketing_agree,
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function save(e) {
    e.preventDefault();
    setBusy(true); setError(""); setMsg("");
    try {
      await updateProfile(form);
      setMsg("저장했습니다.");
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  return (
    <form className="auth-card form" onSubmit={save} noValidate>
      <div className="form__field">
        <label className="form__label">이메일</label>
        <input className="form__input" value={member.email} disabled readOnly />
        <p className="form__hint">
          {member.email_verified ? "이메일 인증 완료" : "이메일 인증이 아직 끝나지 않았습니다."}
        </p>
      </div>

      <div className="form__row">
        <div className="form__field">
          <label className="form__label" htmlFor="ac-name">이름</label>
          <input id="ac-name" className="form__input" value={form.name} onChange={upd("name")} disabled={busy} maxLength={60} />
        </div>
        <div className="form__field">
          <label className="form__label" htmlFor="ac-phone">연락처</label>
          <input id="ac-phone" className="form__input" type="tel" value={form.phone} onChange={upd("phone")} disabled={busy} maxLength={40} />
        </div>
      </div>

      <div className="form__field">
        <label className="form__label" htmlFor="ac-company">회사 · 기관명</label>
        <input id="ac-company" className="form__input" value={form.company} onChange={upd("company")} disabled={busy} maxLength={200} />
      </div>

      <div className="form__agree">
        <input
          id="ac-mkt" type="checkbox" checked={form.marketing_agree} disabled={busy}
          onChange={(e) => setForm((f) => ({ ...f, marketing_agree: e.target.checked }))}
        />
        <label htmlFor="ac-mkt">신제품·자료 소식 메일을 받겠습니다.</label>
      </div>

      {error && <p className="form__error" role="alert">{error}</p>}
      {msg && <p className="form__hint" role="status">{msg}</p>}

      <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
        {busy ? "저장 중…" : "저장"}
      </button>
    </form>
  );
}

function PasswordChange() {
  const { call } = useAuth();
  const [form, setForm] = useState({ current: "", a: "", b: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function save(e) {
    e.preventDefault();
    if (form.a !== form.b) { setError("새 비밀번호가 서로 다릅니다."); return; }
    if (form.a.length < 8) { setError("비밀번호는 8자 이상으로 정해주세요."); return; }
    setBusy(true); setError(""); setMsg("");
    try {
      await call("/me/password", {
        method: "POST",
        body: JSON.stringify({ current_password: form.current, new_password: form.a }),
      });
      setForm({ current: "", a: "", b: "" });
      setMsg("비밀번호를 변경했습니다. 보안을 위해 다시 로그인해주세요.");
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  return (
    <form className="auth-card form" onSubmit={save} noValidate>
      <div className="form__field">
        <label className="form__label" htmlFor="pc-cur">현재 비밀번호</label>
        <input id="pc-cur" className="form__input" type="password" autoComplete="current-password"
          value={form.current} onChange={upd("current")} disabled={busy} />
      </div>
      <div className="form__row">
        <div className="form__field">
          <label className="form__label" htmlFor="pc-a">새 비밀번호</label>
          <input id="pc-a" className="form__input" type="password" autoComplete="new-password"
            value={form.a} onChange={upd("a")} disabled={busy} minLength={8} />
        </div>
        <div className="form__field">
          <label className="form__label" htmlFor="pc-b">새 비밀번호 확인</label>
          <input id="pc-b" className="form__input" type="password" autoComplete="new-password"
            value={form.b} onChange={upd("b")} disabled={busy} minLength={8} />
        </div>
      </div>

      {error && <p className="form__error" role="alert">{error}</p>}
      {msg && <p className="form__hint" role="status">{msg}</p>}

      <button type="submit" className="btn btn--ghost btn--block" disabled={busy}>
        {busy ? "변경 중…" : "비밀번호 변경"}
      </button>
    </form>
  );
}

function Withdraw() {
  const { call, refresh } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function go(e) {
    e.preventDefault();
    if (!window.confirm("정말 탈퇴하시겠습니까? 회원 정보는 즉시 삭제되며 되돌릴 수 없습니다.")) return;
    setBusy(true); setError("");
    try {
      await call("/me/withdraw", { method: "POST", body: JSON.stringify({ password }) });
      await refresh();
      navigate("/", { replace: true });
    } catch (err) { setError(err.message); setBusy(false); }
  }

  if (!open) {
    return (
      <p className="note-line">
        더 이상 이용하지 않으시겠습니까?{" "}
        <button type="button" className="link-btn" onClick={() => setOpen(true)}>회원 탈퇴</button>
      </p>
    );
  }

  return (
    <form className="auth-card form" onSubmit={go} noValidate>
      <p className="form__hint" style={{ marginBottom: 14 }}>
        탈퇴하시면 이름·이메일·연락처 등 회원 정보가 즉시 삭제되며 복구할 수 없습니다.
        확인을 위해 비밀번호를 입력해주세요.
      </p>
      <div className="form__field">
        <label className="form__label" htmlFor="wd-pw">비밀번호</label>
        <input id="wd-pw" className="form__input" type="password" autoComplete="current-password"
          value={password} onChange={(e) => setPassword(e.target.value)} disabled={busy} />
      </div>
      {error && <p className="form__error" role="alert">{error}</p>}
      <div className="form__row">
        <button type="submit" className="btn btn--ghost btn--block" disabled={busy}>
          {busy ? "처리 중…" : "탈퇴하기"}
        </button>
        <button type="button" className="btn btn--ghost btn--block" onClick={() => setOpen(false)} disabled={busy}>
          취소
        </button>
      </div>
    </form>
  );
}

export default function Account() {
  useSeo({ title: "내 정보 | I.META", path: "/account", noindex: true });

  const { member, ready, logout, call } = useAuth();
  const navigate = useNavigate();
  const [resendMsg, setResendMsg] = useState("");

  if (!ready) {
    return <section className="section"><p className="note-line">불러오는 중…</p></section>;
  }

  if (!member) {
    return (
      <PageHero kicker="ACCOUNT" title="로그인이 필요합니다" desc="내 정보를 보려면 먼저 로그인해주세요.">
        <div className="hero__actions">
          <Link to="/login" className="btn btn--primary" state={{ from: "/account" }}>
            로그인 <Icon.ArrowRight width="16" height="16" aria-hidden="true" focusable="false" />
          </Link>
          <Link to="/signup" className="btn btn--ghost">회원가입</Link>
        </div>
      </PageHero>
    );
  }

  async function resend() {
    setResendMsg("");
    try {
      await call("/verify/resend", { method: "POST" });
      setResendMsg("인증 메일을 다시 보내드렸습니다.");
    } catch (err) { setResendMsg(err.message); }
  }

  return (
    <>
      <PageHero kicker="ACCOUNT" title="내 정보" desc={`${member.name} 님, 안녕하세요.`} />

      {!member.email_verified && (
        <section className="section section--tight">
          <div className="notice">
            <p>
              이메일 인증이 아직 끝나지 않았습니다. 보내드린 메일의 링크를 눌러 인증을 마쳐주세요.{" "}
              <button type="button" className="link-btn" onClick={resend}>인증 메일 다시 받기</button>
            </p>
            {resendMsg && <p className="form__hint">{resendMsg}</p>}
          </div>
        </section>
      )}

      <section className="section section--tight">
        <SectionHead kicker="PROFILE" title="회원 정보" />
        <Profile />
      </section>

      <section className="section section--tight">
        <SectionHead kicker="PASSWORD" title="비밀번호 변경" />
        <PasswordChange />
      </section>

      <section className="section section--tight">
        <div className="auth-actions">
          <button
            type="button" className="btn btn--ghost"
            onClick={async () => { await logout(); navigate("/", { replace: true }); }}
          >
            로그아웃
          </button>
        </div>
        <Withdraw />
      </section>
    </>
  );
}
